import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type AttentionMetricsInsert = Database['public']['Tables']['attention_metrics']['Insert'];
type AttentionReportsInsert = Database['public']['Tables']['attention_reports']['Insert'];
type KaiInsightsInsert = Database['public']['Tables']['kai_insights']['Insert'];

export interface AttentionData {
  passiveSeconds: number;
  scrollDepthPercent: number;
  expandClicks: number;
  questionClicks: number;
  personClicks: number;
  companyClicks: number;
  tagClicks: number;
  contextClicks: number;
  kaiSeconds: number;
  kaiMessages: number;
  quizSeconds: number;
  quizCompleted: boolean;
  quizScore: number | null;
  pollAnswered: boolean;
  debateSeconds: number;
  debateArguments: number;
  debateVotes: number;
  tabVisits: Record<string, number>;
  zoneVisibility: Record<string, number>;
}

export interface AttentionMetrics {
  id: string;
  session_id: string;
  interview_id: string;
  user_id: string | null;
  passive_seconds: number;
  scroll_depth_percent: number;
  expand_clicks: number;
  question_clicks: number;
  person_clicks: number;
  company_clicks: number;
  tag_clicks: number;
  context_clicks: number;
  kai_seconds: number;
  kai_messages: number;
  quiz_seconds: number;
  quiz_completed: boolean;
  quiz_score: number | null;
  poll_answered: boolean;
  debate_seconds: number;
  debate_arguments: number;
  debate_votes: number;
  tab_visits: Record<string, number>;
  zone_visibility: Record<string, number>;
  total_attention_value: number;
  device_info: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// TAV weights from the plan
const TAV_WEIGHTS = {
  passive: 1.0,
  kai: 2.5,
  quiz: 2.5,
  debate: 2.0,
  poll: 1.5,
  expandClick: 5,
  personClick: 3,
  tagClick: 2,
  questionClick: 2,
  companyClick: 2,
  contextClick: 1,
} as const;

/**
 * Calculate Total Attention Value (TAV)
 * TAV = (T_passiv × 1.0) + (T_kai × 2.5) + (T_quiz × 2.5) + (T_debatt × 2.0) + (T_poll × 1.5)
 *     + (klikk_expand × 5) + (klikk_person × 3) + (klikk_hashtag × 2)
 */
export function calculateTAV(data: AttentionData): number {
  return (
    data.passiveSeconds * TAV_WEIGHTS.passive +
    data.kaiSeconds * TAV_WEIGHTS.kai +
    data.quizSeconds * TAV_WEIGHTS.quiz +
    data.debateSeconds * TAV_WEIGHTS.debate +
    (data.pollAnswered ? 30 * TAV_WEIGHTS.poll : 0) + // Estimate 30 seconds for poll
    data.expandClicks * TAV_WEIGHTS.expandClick +
    data.personClicks * TAV_WEIGHTS.personClick +
    data.tagClicks * TAV_WEIGHTS.tagClick +
    data.questionClicks * TAV_WEIGHTS.questionClick +
    data.companyClicks * TAV_WEIGHTS.companyClick +
    data.contextClicks * TAV_WEIGHTS.contextClick
  );
}

/**
 * Get or create attention metrics record for a session/interview
 */
export async function getOrCreateAttentionMetrics(
  sessionId: string,
  interviewId: string,
  userId?: string,
  deviceInfo?: Record<string, unknown>
): Promise<string | null> {
  try {
    // Try to find existing record
    const { data: existing, error: selectError } = await supabase
      .from('attention_metrics')
      .select('id')
      .eq('session_id', sessionId)
      .eq('interview_id', interviewId)
      .maybeSingle();

    if (selectError) {
      console.error('Error checking existing attention metrics:', selectError);
      return null;
    }

    if (existing) {
      return existing.id;
    }

    // Create new record
    const insertData: AttentionMetricsInsert = {
      session_id: sessionId,
      interview_id: interviewId,
      user_id: userId || null,
      device_info: JSON.parse(JSON.stringify(deviceInfo || {})),
    };
    const { data: newRecord, error: insertError } = await supabase
      .from('attention_metrics')
      .insert(insertData)
      .select('id')
      .single();

    if (insertError) {
      console.error('Error creating attention metrics:', insertError);
      return null;
    }

    return newRecord.id;
  } catch (error) {
    console.error('Error in getOrCreateAttentionMetrics:', error);
    return null;
  }
}

/**
 * Update attention metrics with accumulated data
 */
export async function updateAttentionMetrics(
  sessionId: string,
  interviewId: string,
  data: Partial<AttentionData>
): Promise<boolean> {
  try {
    // First get current values
    const { data: current, error: selectError } = await supabase
      .from('attention_metrics')
      .select('*')
      .eq('session_id', sessionId)
      .eq('interview_id', interviewId)
      .maybeSingle();

    if (selectError) {
      console.error('Error fetching current metrics:', selectError);
      return false;
    }

    // Build update object with accumulated values
    const updates: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    if (data.passiveSeconds !== undefined) {
      updates.passive_seconds = (current?.passive_seconds || 0) + data.passiveSeconds;
    }
    if (data.scrollDepthPercent !== undefined) {
      updates.scroll_depth_percent = Math.max(current?.scroll_depth_percent || 0, data.scrollDepthPercent);
    }
    if (data.expandClicks !== undefined) {
      updates.expand_clicks = (current?.expand_clicks || 0) + data.expandClicks;
    }
    if (data.questionClicks !== undefined) {
      updates.question_clicks = (current?.question_clicks || 0) + data.questionClicks;
    }
    if (data.personClicks !== undefined) {
      updates.person_clicks = (current?.person_clicks || 0) + data.personClicks;
    }
    if (data.companyClicks !== undefined) {
      updates.company_clicks = (current?.company_clicks || 0) + data.companyClicks;
    }
    if (data.tagClicks !== undefined) {
      updates.tag_clicks = (current?.tag_clicks || 0) + data.tagClicks;
    }
    if (data.contextClicks !== undefined) {
      updates.context_clicks = (current?.context_clicks || 0) + data.contextClicks;
    }
    if (data.kaiSeconds !== undefined) {
      updates.kai_seconds = (current?.kai_seconds || 0) + data.kaiSeconds;
    }
    if (data.kaiMessages !== undefined) {
      updates.kai_messages = (current?.kai_messages || 0) + data.kaiMessages;
    }
    if (data.quizSeconds !== undefined) {
      updates.quiz_seconds = (current?.quiz_seconds || 0) + data.quizSeconds;
    }
    if (data.quizCompleted !== undefined) {
      updates.quiz_completed = data.quizCompleted;
    }
    if (data.quizScore !== undefined) {
      updates.quiz_score = data.quizScore;
    }
    if (data.pollAnswered !== undefined) {
      updates.poll_answered = data.pollAnswered;
    }
    if (data.debateSeconds !== undefined) {
      updates.debate_seconds = (current?.debate_seconds || 0) + data.debateSeconds;
    }
    if (data.debateArguments !== undefined) {
      updates.debate_arguments = (current?.debate_arguments || 0) + data.debateArguments;
    }
    if (data.debateVotes !== undefined) {
      updates.debate_votes = (current?.debate_votes || 0) + data.debateVotes;
    }
    if (data.tabVisits !== undefined) {
      const currentTabVisits = (current?.tab_visits as Record<string, number>) || {};
      const newTabVisits = { ...currentTabVisits };
      Object.entries(data.tabVisits).forEach(([tab, count]) => {
        newTabVisits[tab] = (newTabVisits[tab] || 0) + count;
      });
      updates.tab_visits = newTabVisits;
    }
    if (data.zoneVisibility !== undefined) {
      const currentZones = (current?.zone_visibility as Record<string, number>) || {};
      const newZones = { ...currentZones };
      Object.entries(data.zoneVisibility).forEach(([zone, seconds]) => {
        newZones[zone] = (newZones[zone] || 0) + seconds;
      });
      updates.zone_visibility = newZones;
    }

    // Calculate new TAV
    const fullData: AttentionData = {
      passiveSeconds: (updates.passive_seconds as number) ?? current?.passive_seconds ?? 0,
      scrollDepthPercent: (updates.scroll_depth_percent as number) ?? current?.scroll_depth_percent ?? 0,
      expandClicks: (updates.expand_clicks as number) ?? current?.expand_clicks ?? 0,
      questionClicks: (updates.question_clicks as number) ?? current?.question_clicks ?? 0,
      personClicks: (updates.person_clicks as number) ?? current?.person_clicks ?? 0,
      companyClicks: (updates.company_clicks as number) ?? current?.company_clicks ?? 0,
      tagClicks: (updates.tag_clicks as number) ?? current?.tag_clicks ?? 0,
      contextClicks: (updates.context_clicks as number) ?? current?.context_clicks ?? 0,
      kaiSeconds: (updates.kai_seconds as number) ?? current?.kai_seconds ?? 0,
      kaiMessages: (updates.kai_messages as number) ?? current?.kai_messages ?? 0,
      quizSeconds: (updates.quiz_seconds as number) ?? current?.quiz_seconds ?? 0,
      quizCompleted: (updates.quiz_completed as boolean) ?? current?.quiz_completed ?? false,
      quizScore: (updates.quiz_score as number | null) ?? current?.quiz_score ?? null,
      pollAnswered: (updates.poll_answered as boolean) ?? current?.poll_answered ?? false,
      debateSeconds: (updates.debate_seconds as number) ?? current?.debate_seconds ?? 0,
      debateArguments: (updates.debate_arguments as number) ?? current?.debate_arguments ?? 0,
      debateVotes: (updates.debate_votes as number) ?? current?.debate_votes ?? 0,
      tabVisits: (updates.tab_visits as Record<string, number>) ?? (current?.tab_visits as Record<string, number> | null) ?? {},
      zoneVisibility: (updates.zone_visibility as Record<string, number>) ?? (current?.zone_visibility as Record<string, number> | null) ?? {},
    };

    updates.total_attention_value = calculateTAV(fullData);

    if (current) {
      // Update existing record
      const { error: updateError } = await supabase
        .from('attention_metrics')
        .update(updates as Record<string, unknown>)
        .eq('session_id', sessionId)
        .eq('interview_id', interviewId);

      if (updateError) {
        console.error('Error updating attention metrics:', updateError);
        return false;
      }
    } else {
      // Create new record with updates
      const insertData: AttentionMetricsInsert = {
        session_id: sessionId,
        interview_id: interviewId,
        passive_seconds: updates.passive_seconds as number | undefined,
        scroll_depth_percent: updates.scroll_depth_percent as number | undefined,
        expand_clicks: updates.expand_clicks as number | undefined,
        question_clicks: updates.question_clicks as number | undefined,
        person_clicks: updates.person_clicks as number | undefined,
        company_clicks: updates.company_clicks as number | undefined,
        tag_clicks: updates.tag_clicks as number | undefined,
        context_clicks: updates.context_clicks as number | undefined,
        kai_seconds: updates.kai_seconds as number | undefined,
        kai_messages: updates.kai_messages as number | undefined,
        quiz_seconds: updates.quiz_seconds as number | undefined,
        quiz_completed: updates.quiz_completed as boolean | undefined,
        quiz_score: updates.quiz_score as number | null | undefined,
        poll_answered: updates.poll_answered as boolean | undefined,
        debate_seconds: updates.debate_seconds as number | undefined,
        debate_arguments: updates.debate_arguments as number | undefined,
        debate_votes: updates.debate_votes as number | undefined,
        tab_visits: updates.tab_visits ? JSON.parse(JSON.stringify(updates.tab_visits)) : undefined,
        zone_visibility: updates.zone_visibility ? JSON.parse(JSON.stringify(updates.zone_visibility)) : undefined,
        total_attention_value: updates.total_attention_value as number | undefined,
      };
      const { error: insertError } = await supabase
        .from('attention_metrics')
        .insert(insertData);

      if (insertError) {
        console.error('Error inserting attention metrics:', insertError);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error in updateAttentionMetrics:', error);
    return false;
  }
}

/**
 * Track a specific click type
 */
export async function trackClick(
  sessionId: string,
  interviewId: string,
  clickType: 'expand' | 'question' | 'person' | 'company' | 'tag' | 'context'
): Promise<void> {
  const updates: Partial<AttentionData> = {};
  
  switch (clickType) {
    case 'expand':
      updates.expandClicks = 1;
      break;
    case 'question':
      updates.questionClicks = 1;
      break;
    case 'person':
      updates.personClicks = 1;
      break;
    case 'company':
      updates.companyClicks = 1;
      break;
    case 'tag':
      updates.tagClicks = 1;
      break;
    case 'context':
      updates.contextClicks = 1;
      break;
  }

  await updateAttentionMetrics(sessionId, interviewId, updates);
}

/**
 * Track Kai query for Zero-Party Data insights
 */
export async function trackKaiQuery(
  interviewId: string,
  queryText: string,
  category?: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('kai_insights')
      .insert({
        interview_id: interviewId,
        query_text: queryText,
        query_category: category || null,
      });

    if (error) {
      console.error('Error tracking Kai query:', error);
    }
  } catch (error) {
    console.error('Error in trackKaiQuery:', error);
  }
}

/**
 * Track tab visit
 */
export async function trackTabVisit(
  sessionId: string,
  interviewId: string,
  tabName: string
): Promise<void> {
  await updateAttentionMetrics(sessionId, interviewId, {
    tabVisits: { [tabName]: 1 },
  });
}

/**
 * Track zone visibility time
 */
export async function trackZoneVisibility(
  sessionId: string,
  interviewId: string,
  zoneId: string,
  seconds: number
): Promise<void> {
  await updateAttentionMetrics(sessionId, interviewId, {
    zoneVisibility: { [zoneId]: seconds },
  });
}
